// Player avatars (food/drink themed)
export const AVATARS = [
    '🍺', '🍷', '🍸', '🍹', '🥂', '🥃', '🥤', '🧉',
    '🍕', '🌮', '🍔', '🍟', '🍩', '🍦', '🥑', '🥦',
    '🌶️', '🧀'
];
// Room code pool (beer-themed 4-letter codes)
export const ROOM_CODES = [
    'BEER', 'WINE', 'SHOT', 'FIZZ', 'MULE', 'SOUR', 'TAIL', 'SAKE',
    'GINS', 'VODK', 'RUMS', 'PORT', 'MEAD', 'HOPS', 'MALT', 'CHUG',
    'GULP', 'SIPY', 'LIME', 'SALT', 'COOL', 'CHEF', 'RARE', 'DRNK'
];
// Timing constants (in milliseconds)
export const ROUND_DURATION_MS = 15000;
export const RESULT_DURATION_MS = 5000;
export const SCOREBOARD_DURATION_MS = 6000;
export const NICHE_ANALYSIS_DURATION_MS = 4000;
export const PENALTY_SPIN_DURATION_MS = 4500;
// Session recovery window (1 hour)
export const SESSION_RECOVERY_MS = 3600000;
// Default game configuration
export const DEFAULT_GAME_CONFIG = {
    votesPerPlayer: 5,
    vetoRemovalCount: 1,
    nicheRemovalCount: 0,
    maxTotalQuestions: 30,
    categoriesPerPlayer: 2,
    devMode: false
};
// Get round duration based on dev mode
// Dev mode: returns 0 to signal infinite (no timer)
// Normal mode: returns 15 seconds
export const getRoundDuration = (devMode) => {
    return devMode ? 0 : ROUND_DURATION_MS;
};
// Minimum categories that must survive all removal phases
export const MIN_SURVIVING_CATEGORIES = 3;
// Configuration limits
export const CONFIG_LIMITS = {
    votesPerPlayer: { min: 1, max: 10 },
    vetoRemovalCount: { min: 0, max: 15 },
    nicheRemovalCount: { min: 0, max: 10 },
    maxTotalQuestions: { min: 3, max: 60 },
    categoriesPerPlayer: { min: 1, max: 5 },
    maxNickname: 20,
    maxPenalty: 40,
    minCategory: 2,
    maxPlayers: 20,
    minPlayers: 2
};
// Compute the max total removals (veto + niche) for a given player/category setup.
// Guarantees at least MIN_SURVIVING_CATEGORIES survive.
export const getMaxTotalRemovals = (playerCount, categoriesPerPlayer) => {
    const totalCategories = playerCount * categoriesPerPlayer;
    return Math.max(0, totalCategories - MIN_SURVIVING_CATEGORIES);
};
// Initial game state factory
export const createInitialGameState = () => ({
    status: 'LANDING',
    roomCode: '',
    hostId: '',
    players: [],
    categories: [],
    questions: [],
    currentQuestionIndex: 0,
    currentRoundEndTime: 0,
    playerAnswers: {},
    lastRoast: null,
    nicheCategory: null,
    nicheCommentary: null,
    vetoCommentary: null,
    loserId: null,
    winnerId: null,
    finalPenalty: null,
    penaltiesPool: [],
    isPenaltySpinning: false,
    config: { ...DEFAULT_GAME_CONFIG }
});
// Scoring formula
export const calculateScore = (reactionTimeMs, isCorrect) => {
    if (!isCorrect)
        return 0;
    // Points = max(10, 100 - floor(reactionTime / 150))
    return Math.max(10, 100 - Math.floor(reactionTimeMs / 150));
};
// Error codes
export const ERROR_CODES = {
    ROOM_NOT_FOUND: 'ROOM_NOT_FOUND',
    ROOM_FULL: 'ROOM_FULL',
    GAME_IN_PROGRESS: 'GAME_IN_PROGRESS',
    INVALID_ACTION: 'INVALID_ACTION',
    NOT_HOST: 'NOT_HOST',
    PLAYER_NOT_FOUND: 'PLAYER_NOT_FOUND',
    INVALID_PHASE: 'INVALID_PHASE',
    ALREADY_ANSWERED: 'ALREADY_ANSWERED',
    RECONNECT_FAILED: 'RECONNECT_FAILED',
    RATE_LIMITED: 'RATE_LIMITED'
};
// Waiting screen messages
export const WAITING_MESSAGES = [
    'Mixing logic and ice...',
    'Roasting the slow pokes...',
    'Calibrating the fun meter...',
    'Warming up the neurons...',
    'Polishing the quiz crystals...',
    'Shuffling the deck of fate...',
    'Brewing the questions...',
    'Loading sarcasm module...'
];
// Generating screen messages
export const GENERATING_MESSAGES = [
    'Mixing drinks...',
    'Googling things...',
    'Adding ice...',
    'Distilling knowledge...',
    'Fermenting questions...',
    'Aging to perfection...',
    'Stirring the pot...',
    'Consulting the oracle...'
];
// Scoreboard reveal messages
export const SCOREBOARD_MESSAGES = {
    calibrating: 'Calibrating shame meter...',
    revealing: 'Revealing survivors...',
    champion: 'Behold the champion...',
    sacrifice: 'Finding the sacrifice...',
    done: 'Judgment has been served'
};
// Player position labels
export const POSITION_LABELS = {
    winner: 'The Grand Master',
    middle: 'Average Human',
    loser: 'The Designated Victim'
};
// Roast fallbacks (when AI fails)
export const ROAST_FALLBACKS = [
    'Even a goldfish would have gotten that one.',
    'Your brain is officially on battery saver mode.',
    'Are you even trying, or just clicking randomly?',
    'That was impressively wrong. Truly an art form.',
    "I've seen smarter decisions made by a drunken pigeon.",
    'Confidence: 100. Accuracy: 0. Common sense: Not found.'
];
// Category suggestion fallbacks
export const CATEGORY_FALLBACKS = [
    '90s Nostalgia',
    'Fast Food Facts',
    'Classic Movie Tropes',
    'Pop Music History',
    'Famous Landmarks',
    'Tech Giant Origins',
    'Bizarre Animal Traits',
    '80s Arcade Games'
];
